import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  retryAisensyConnection,
  refreshWhatsappTokenDebug,
  syncAllPhoneHealth,
  refreshProfilePictures,
} from "../apis/whatsappAccount";
import api from "../services/api";
import ManualConnectModal from "../components/whatsapp/ManualConnectModal";
import AisensyBusinessSetupModal from "../components/whatsapp/AisensyBusinessSetupModal";
import ConnectMethodPicker from "../components/whatsapp/ConnectMethodPicker";
import ConnectResultBanner from "../components/whatsapp/ConnectResultBanner";
import WhatsappConnectOverlay from "../components/whatsapp/WhatsappConnectOverlay";
import ConnectionTypeBadge from "../components/whatsapp/ConnectionTypeBadge";
import PhoneProfileAvatar from "../components/whatsapp/PhoneProfileAvatar";
import {
  getEmbeddedSignupLoginOptions,
  buildEmbeddedSignupCallbackBodyAsync,
  installEmbeddedSignupMessageListener,
  clearEmbeddedSignupSession,
} from "../config/facebookEmbeddedSignup";
import {
  formatMessagingLimit,
  formatQualityRating,
  formatNameStatus,
  qualityTone,
  nameStatusTone,
} from "../utils/phoneHealthLabels";
import useSubscriptionAccess, { invalidateSubscriptionAccess } from "../hooks/useSubscriptionAccess";
import { logEvent } from "../apis/events";
import { toast } from "react-toastify";
import SubscribeRequiredPrompt from "../components/SubscribeRequiredPrompt";
import { createAddonOrder, verifyAddonPayment } from "../apis/subscription";
import { loadScript, openRazorpayCheckout } from "../utils/razorpay";
import {
  ALREADY_CONNECTED_TITLE,
  alreadyConnectedMessage,
  connectResultFromSignupPayload,
  takeStashedWabaConnectResult,
} from "../utils/whatsappConnectErrors";

const WhatsappBusiness = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: accessLoading, hasAccess, numberLimit, refresh: refreshAccess } = useSubscriptionAccess();
  const needsPlan = !accessLoading && !hasAccess;
  const atPlanCap = Boolean(hasAccess && numberLimit && !numberLimit.canAdd);
  const [fetching, setFetching] = useState(false);
  const [overlayType, setOverlayType] = useState(null);
  const [connected, setConnected] = useState(false);
  const [pageError, setPageError] = useState("");
  const [connectResult, setConnectResult] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [retryingPhoneId, setRetryingPhoneId] = useState(null);
  const [connectPickerOpen, setConnectPickerOpen] = useState(false);
  const [subscribePromptOpen, setSubscribePromptOpen] = useState(false);
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [aisensyBizModalOpen, setAisensyBizModalOpen] = useState(false);
  const [aisensyBizSubmitting, setAisensyBizSubmitting] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState(null);
  const [refreshingPhoneId, setRefreshingPhoneId] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [buyingExtraNumber, setBuyingExtraNumber] = useState(false);

  const fetchWhatsappAccounts = async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      const res = await api.get("/api/whatsapp-accounts", {
        params: { section: "phone_numbers" },
      });

      const data = res.data;
      if (data.success) {
        let list = data.data || [];
        setAccounts(list);
        if (list.length > 0) setConnected(true);

        // Backfill missing DPs from Meta (async — update cards when done)
        const missing = list.filter((a) => !a.profile_picture_url);
        if (missing.length > 0) {
          refreshProfilePictures()
            .then((picRes) => {
              const pictures = picRes.data?.data?.pictures || [];
              if (!pictures.length) return;
              const byId = Object.fromEntries(
                pictures.map((p) => [String(p.phone_id), p.profile_picture_url])
              );
              setAccounts((prev) =>
                prev.map((a) =>
                  byId[String(a.phone_id)]
                    ? { ...a, profile_picture_url: byId[String(a.phone_id)] }
                    : a
                )
              );
            })
            .catch(() => {});
        }
      } else {
        setPageError(data.error || "Failed to load WhatsApp accounts");
      }
    } catch (err) {
      setPageError("Server error while fetching WhatsApp accounts");
    } finally {
      setFetching(false);
    }
  };

  const handleSyncAllHealth = async () => {
    try {
      setSyncingAll(true);
      setPageError("");
      const res = await syncAllPhoneHealth();
      if (res.data?.success) {
        setConnectResult({
          type: "success",
          title: "Health synced",
          message: "Quality, messaging limits, and webhook subscriptions updated for your numbers.",
        });
        await fetchWhatsappAccounts();
      } else {
        setPageError(res.data?.error || "Failed to sync phone health");
      }
    } catch (err) {
      setPageError(err.response?.data?.error || "Failed to sync phone health");
    } finally {
      setSyncingAll(false);
    }
  };

  // Retry AiSensy procurement (Business → Project → submit WABA → JWT → webhook)
  const handleRetryAisensy = async (phoneId) => {
    try {
      setRetryingPhoneId(phoneId);
      const token = localStorage.getItem("token");
      if (!token) {
        setConnectResult({
          type: "error",
          title: "Authentication required",
          message: "Please log in again to continue.",
        });
        return;
      }

      const response = await retryAisensyConnection(phoneId);
      if (response.data.success) {
        setConnectResult({
          type: "success",
          title: "WhatsApp setup complete",
          message: "Number is now live on Blutec Ping and ready for messaging.",
        });
        fetchWhatsappAccounts();
      } else {
        setPageError(
          [
            response.data.message ||
              response.data.error ||
              "WhatsApp setup is still pending. Please try again shortly.",
            response.data.action_required,
          ]
            .filter(Boolean)
            .join(" ")
        );
        fetchWhatsappAccounts();
      }
    } catch (err) {
      setPageError(
        [
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Server error while retrying WhatsApp setup. Please try again.",
          err.response?.data?.action_required,
        ]
          .filter(Boolean)
          .join(" ")
      );
      fetchWhatsappAccounts();
    } finally {
      setRetryingPhoneId(null);
    }
  };

  // Delete WhatsApp Phone Number
  const getDisconnectConfirmMessage = () => {
    return [
      "Disconnect this number?",
      "",
      "• The number will be removed from Blutec Ping",
      "• Messaging for this number will stop in Blutec Ping",
      "• The ₹100 monthly charge stops for this number only. Other numbers on the account keep billing.",
      "• This does not free a number-add slot this cycle — connecting another number still uses quota (or a paid add-on). Quota resets when the plan renews.",
    ].join("\n");
  };

  const handleDeletePhoneNumber = async (phone_id) => {
    if (!window.confirm(getDisconnectConfirmMessage())) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      setOverlayType("disconnecting");

      const response = await api.delete(`/api/whatsapp-accounts/${phone_id}`);

      const data = response.data;

      if (data.success) {
        await fetchWhatsappAccounts();
        window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
        setConnectResult({
          type: data.partial ? "partial" : "success",
          title: data.partial ? "Disconnected with warnings" : "Disconnected",
          message: data.message || "WhatsApp number disconnected successfully.",
          setupSteps: data.steps || [],
        });
        invalidateSubscriptionAccess();
        refreshAccess();
      } else {
        setConnectResult({
          type: "error",
          title: "Disconnect failed",
          message: data.message || "Something went wrong.",
        });
      }
    } catch (err) {
      setConnectResult({
        type: "error",
        title: "Disconnect failed",
        message: "Server error while disconnecting phone number.",
      });
    } finally {
      setOverlayType(null);
    }
  };

  const handleBuyExtraNumber = async () => {
    if (needsPlan) {
      toast.info("Subscribe to a plan first. Wallet balance cannot be used to add a WhatsApp number.");
      setSubscribePromptOpen(true);
      return;
    }
    if (!numberLimit?.addonPlanKey) return;
    setBuyingExtraNumber(true);
    try {
      await loadScript();
      const orderResponse = await createAddonOrder(numberLimit.addonPlanKey, "monthly");
      if (!orderResponse.data?.success) {
        throw new Error(orderResponse.data?.error || "Failed to create order");
      }
      const { order_id, amount, key_id } = orderResponse.data.data || {};
      openRazorpayCheckout({
        key: key_id,
        amount,
        orderId: order_id,
        description: "Extra WhatsApp number add-on",
        handler: async (response) => {
          try {
            const verifyResponse = await verifyAddonPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (!verifyResponse.data?.success) {
              throw new Error(verifyResponse.data?.error || "Payment verification failed");
            }
            toast.success("Extra number slot added. You can connect another WhatsApp number.");
            invalidateSubscriptionAccess();
            await refreshAccess();
          } catch (err) {
            toast.error(err.response?.data?.error || err.message || "Payment verification failed");
          } finally {
            setBuyingExtraNumber(false);
          }
        },
        onDismiss: () => setBuyingExtraNumber(false),
      });
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Could not start checkout");
      setBuyingExtraNumber(false);
    }
  };

  // Send code to backend
  const sendCodeToBackend = async (code) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setConnectResult({
        type: "error",
        title: "Authentication required",
        message: "Please log in again to continue.",
      });
      return;
    }

    try {
      setOverlayType("adding");
      setConnectResult(null);

      const body = await buildEmbeddedSignupCallbackBodyAsync(code);
      if (body.cancelled) {
        setOverlayType(null);
        setConnectResult({
          type: "error",
          title: "Signup cancelled",
          message: "WhatsApp signup was cancelled.",
        });
        clearEmbeddedSignupSession();
        return;
      }

      const res = await api.post("/auth/facebook/embedded-signup/callback", body);
      clearEmbeddedSignupSession();
      const data = res.data;

      await fetchWhatsappAccounts();
      window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
      invalidateSubscriptionAccess();
      refreshAccess();
      setOverlayType(null);

      const blocked = connectResultFromSignupPayload(data);
      if (blocked) {
        setConnectResult(blocked);
      } else if (data.success) {
        const processedCount = data.processed?.length || 0;
        if (data.partial) {
          setConnectResult({
            type: "partial",
            title: `${processedCount} number(s) connected (partial)`,
            message: data.message || "Some setup steps had warnings.",
            setupSteps: buildEmbeddedSetupSteps(data),
          });
        } else {
          setConnectResult({
            type: "success",
            title: "Connected via Facebook",
            message: `${processedCount} WhatsApp number(s) added and activated successfully.`,
            setupSteps: buildEmbeddedSetupSteps(data),
          });
        }
      } else {
        setConnectResult({
          type: "error",
          title: data.error_title || "Could not connect WhatsApp",
          message: data.message || data.error || "Failed to complete onboarding.",
        });
      }
    } catch (error) {
      const payload = error.response?.data || {};
      const blocked = connectResultFromSignupPayload(payload);
      const metaMsg = payload?.details?.error?.message;
      const details = typeof payload?.details === "string" ? payload.details : metaMsg;
      setOverlayType(null);
      setConnectResult(
        blocked || {
          type: "error",
          title: payload.error_title || "Could not connect WhatsApp",
          message:
            payload.message ||
            details ||
            payload.error ||
            "Failed to complete onboarding. Please try again.",
        }
      );
      clearEmbeddedSignupSession();
    }
  };

  function buildEmbeddedSetupSteps(data) {
    const steps = [];
    if (data.processed?.length) {
      data.processed.forEach((p) => {
        steps.push({
          step: "aisensy_link",
          status: "success",
          label: `Number live on Blutec Ping: ${p.number || p.phone_id}`,
          message: p.webhook_configured
            ? "Procurement complete and webhook configured"
            : "Procurement complete (webhook configuration pending)",
        });
      });
    }
    (data.aisensyFailures || data.webhookFailures || []).forEach((f) => {
      steps.push({
        step: "aisensy_link",
        status: "failed",
        label:
          f.error_title ||
          `WhatsApp setup incomplete: ${f.number || f.phone_id}`,
        message:
          [f.error || f.message, f.action_required]
            .filter(Boolean)
            .join(" ") ||
          `Setup status: ${f.aisensy_status || "pending"} — use "Retry setup" on the number card.`,
      });
    });
    if (data.warnings?.length) {
      data.warnings.forEach((w) => {
        steps.push({ step: "scope", status: "failed", label: "Scope warning", message: w });
      });
    }
    return steps;
  };

  const openFacebookLogin = () => {
    window.FB.login(
      (response) => {
        if (response.authResponse?.code) {
          sendCodeToBackend(response.authResponse.code);
        } else {
          setConnectResult({
            type: "error",
            title: "Facebook signup failed",
            message: "Failed to get authorization code. Please try again.",
          });
        }
      },
      getEmbeddedSignupLoginOptions()
    );
  };

  const ensureAisensyBusinessThenLogin = async (details = null) => {
    setOverlayType("connecting");
    try {
      const res = await api.post(
        "/auth/facebook/aisensy/ensure-business",
        details || {}
      );
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Could not create your Blutec Ping business");
      }
      setAisensyBizModalOpen(false);
      setOverlayType(null);
      openFacebookLogin();
    } catch (err) {
      setOverlayType(null);
      if (err.response?.data?.needsBusinessDetails) {
        setAisensyBizModalOpen(true);
        return;
      }
      setConnectResult({
        type: "error",
        title: "WhatsApp setup failed",
        message:
          err.response?.data?.error ||
          err.message ||
          "Could not create your Blutec Ping business. Please try again.",
      });
      throw err;
    }
  };

  const handleEmbeddedSignup = async () => {
    if (!accessLoading && !hasAccess) {
      toast.info("You don't have an active subscription. Subscribe to connect your WhatsApp number.");
      setSubscribePromptOpen(true);
      return;
    }
    if (numberLimit && !numberLimit.canAdd) {
      toast.info(`You have used all ${numberLimit.maxNumbers} included number adds this cycle. Buy an extra number to connect another.`);
      handleBuyExtraNumber();
      return;
    }

    setConnectResult(null);
    setPageError("");
    clearEmbeddedSignupSession();

    const token = localStorage.getItem("token");
    if (!token) {
      setConnectResult({
        type: "error",
        title: "Authentication required",
        message: "Please log in to your account first.",
      });
      return;
    }

    if (!window.FB || typeof window.FB.login !== "function") {
      setConnectResult({
        type: "error",
        title: "Facebook SDK unavailable",
        message: "Facebook SDK not loaded. Please refresh and try again.",
      });
      return;
    }

    // Step 1: ensure AiSensy Business (reuse if already linked; else show form)
    try {
      await ensureAisensyBusinessThenLogin(null);
    } catch (_) {
      /* error UI already set; form may be open */
    }
  };

  const handleAisensyBusinessSubmit = async (details) => {
    setAisensyBizSubmitting(true);
    try {
      await ensureAisensyBusinessThenLogin(details);
    } finally {
      setAisensyBizSubmitting(false);
    }
  };

  const handleManualConnectStart = () => {
    setOverlayType("adding-manual");
    setConnectResult(null);
  };

  const handleManualConnectEnd = () => {
    setOverlayType(null);
  };

  const handleRefreshToken = async (phoneId) => {
    try {
      setRefreshingPhoneId(phoneId);
      setConnectResult(null);
      const res = await refreshWhatsappTokenDebug(phoneId, { syncToDb: true });
      const data = res.data;
      if (data.success) {
        await fetchWhatsappAccounts();
        setConnectResult({
          type: data.data?.token?.missing_required_scopes?.length ? "partial" : "success",
          title: "Token refreshed",
          message: data.message || "Token verified and account details synced from Meta.",
          tokenDebug: data.data?.token || null,
        });
      } else {
        setConnectResult({
          type: "error",
          title: "Token refresh failed",
          message: data.error || "Could not debug access token.",
        });
      }
    } catch (err) {
      setConnectResult({
        type: "error",
        title: "Token refresh failed",
        message: err.response?.data?.error || "Server error while debugging token.",
      });
    } finally {
      setRefreshingPhoneId(null);
    }
  };

  const handleManualConnectSuccess = async (data) => {
    await fetchWhatsappAccounts();
    window.dispatchEvent(new CustomEvent("phoneAccountsUpdated"));
    invalidateSubscriptionAccess();
    refreshAccess();

    const baseResult = {
      setupSteps: data?.setupSteps || [],
      webhookNote: data?.webhookNote || null,
      tokenDebug: data?.token || data?.data?.token || null,
    };

    if (data?.partial) {
      setConnectResult({
        ...baseResult,
        type: "partial",
        title: "Number connected (partial)",
        message: data.message || "Number saved but WhatsApp setup is still pending — use Retry setup on the card.",
      });
    } else {
      setConnectResult({
        ...baseResult,
        type: "success",
        title: editPhoneId ? "Credentials updated" : "Number activated",
        message:
          data?.message ||
          (editPhoneId
            ? "WhatsApp credentials updated successfully."
            : "Number connected and set up on Blutec Ping. See steps below for details."),
      });
    }
  };

  const handleManualConnectError = (result) => {
    setConnectResult(result);
  };

  const revealConnectPicker = () => {
    if (!accessLoading && !hasAccess) {
      toast.info("You don't have an active subscription. Subscribe to connect your WhatsApp number.");
      setSubscribePromptOpen(true);
      return;
    }
    if (numberLimit && !numberLimit.canAdd) {
      toast.info(`You have used all ${numberLimit.maxNumbers} included number adds this cycle. Buy an extra number to connect another.`);
      handleBuyExtraNumber();
      return;
    }
    setConnectResult(null);
    setConnectPickerOpen(true);
  };

  const openConnectPicker = () => {
    logEvent("connect_wa_clicked", { source: "picker" });
    revealConnectPicker();
  };

  useEffect(() => {
    const applyResult = (result) => {
      if (!result) return;
      setConnectResult(result);
    };

    applyResult(takeStashedWabaConnectResult());
    const onStash = (event) => applyResult(event.detail);
    window.addEventListener("wabaConnectResult", onStash);

    const params = new URLSearchParams(location.search);
    if (params.get("message") === "number_already_connected") {
      applyResult({
        type: "error",
        title: ALREADY_CONNECTED_TITLE,
        message: alreadyConnectedMessage(),
      });
      navigate("/waba", { replace: true });
    }

    return () => window.removeEventListener("wabaConnectResult", onStash);
  }, [location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("connect") !== "1") return;
    if (accessLoading) return;
    revealConnectPicker();
    navigate("/waba", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open once from nav deep-link
  }, [location.search, accessLoading, hasAccess, numberLimit]);

  const openManualConnect = (phoneId = null) => {
    if (!phoneId) {
      logEvent("connect_wa_clicked", { source: "manual" });
      if (!accessLoading && !hasAccess) {
        toast.info("You don't have an active subscription. Subscribe to connect your WhatsApp number.");
        setSubscribePromptOpen(true);
        return;
      }
      if (numberLimit && !numberLimit.canAdd) {
        toast.info(`You have used all ${numberLimit.maxNumbers} included number adds this cycle. Buy an extra number to connect another.`);
        handleBuyExtraNumber();
        return;
      }
    }
    setEditPhoneId(phoneId);
    setManualModalOpen(true);
  };

  const closeManualConnect = () => {
    setManualModalOpen(false);
    setEditPhoneId(null);
  };

  const handleRetry = () => {
    setPageError("");
    setConnectResult(null);
    fetchWhatsappAccounts();
  };

  // AiSensy procurement not yet live for this number
  const hasProcurementIssue = (account) => {
    if (account.aisensy_procurement_status) {
      return account.aisensy_procurement_status !== "live";
    }
    // Fallback: is_webhook_registered is repurposed as "AiSensy setup complete"
    return account.is_webhook_registered === 0;
  };

  // Get appropriate procurement message (prefer stored friendly error from last attempt)
  const getProcurementMessage = (account) => {
    if (account.webhook_registration_error) {
      return account.webhook_registration_error;
    }
    switch (account.aisensy_procurement_status) {
      case "failed":
        return "WhatsApp setup failed. If this number was connected to another WhatsApp provider, disconnect it there first, then retry.";
      case "submitted":
        return "WhatsApp account submitted — setup is being processed.";
      case "pending":
      default:
        return "WhatsApp setup is pending — the number is not ready for messaging yet.";
    }
  };

  useEffect(() => {
    fetchWhatsappAccounts();
    return installEmbeddedSignupMessageListener();
  }, []);

  const chipTone = (tone) => {
    if (tone === "good") return "g";
    if (tone === "warn") return "a";
    if (tone === "bad") return "r";
    return "n";
  };

  return (
    <div className="waba-list-page">
      <div>
        {/* Header Section */}
        <div className="page-head" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
                WhatsApp Business
              </h2>
              <p style={{ color: "var(--text-2)", fontSize: 14, marginTop: 2 }}>
                {needsPlan
                  ? "Subscribe to a plan to connect a WhatsApp number. Wallet balance cannot be used for this."
                  : numberLimit
                  ? `You can add ${numberLimit.maxNumbers} WhatsApp number${numberLimit.maxNumbers === 1 ? "" : "s"} per cycle on ${numberLimit.planDisplayName || "this plan"} · ${numberLimit.current} of ${numberLimit.maxNumbers} used this cycle`
                  : "Manage your WhatsApp Business accounts"}
              </p>
            </div>
            <div style={{ flex: 1 }} />
            {connected && accounts.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSyncAllHealth}
                  disabled={Boolean(overlayType) || fetching || syncingAll}
                  className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-50"
                >
                  <svg
                    className={`w-4 h-4 ${syncingAll ? "animate-spin" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {syncingAll ? "Syncing…" : "Sync Health"}
                </button>
                <button
                  onClick={openConnectPicker}
                  disabled={Boolean(overlayType) || fetching || atPlanCap}
                  className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
                  title={needsPlan ? "Subscribe to connect a WhatsApp number" : (numberLimit && !numberLimit.canAdd ? `Limit reached (${numberLimit.maxNumbers} number adds)` : "Add a WhatsApp number")}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Number
                </button>
                {atPlanCap ? (
                  <button
                    type="button"
                    onClick={handleBuyExtraNumber}
                    disabled={buyingExtraNumber || Boolean(overlayType)}
                    className="ping-btn ping-btn-green ping-btn-sm disabled:opacity-50"
                  >
                    {buyingExtraNumber
                      ? "Opening checkout…"
                      : `Add extra number · ₹${numberLimit.addonBase || 150}`}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          {needsPlan ? (
            <div
              className="ping-banner"
              style={{
                marginBottom: 14,
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <b>Subscribe to connect a WhatsApp number</b>
                <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                  Wallet recharge only pays for messages. Choose a platform plan first — then you can add a number and start broadcasting.
                </p>
              </div>
              <button
                type="button"
                className="ping-btn ping-btn-green ping-btn-sm"
                onClick={() => setSubscribePromptOpen(true)}
              >
                View plans
              </button>
            </div>
          ) : numberLimit ? (
            <div
              className="ping-banner"
              style={{
                marginBottom: 14,
                background: numberLimit.canAdd ? "#f0fdf4" : "#fff7ed",
                border: `1px solid ${numberLimit.canAdd ? "#bbf7d0" : "#fed7aa"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <b>
                  {numberLimit.canAdd
                    ? `You can add ${numberLimit.remaining} more WhatsApp number${numberLimit.remaining === 1 ? "" : "s"}`
                    : "You have used all included number adds this cycle"}
                </b>
                <p style={{ margin: "4px 0 0", fontSize: 13 }}>
                  {numberLimit.planDisplayName || "Your plan"} includes {numberLimit.maxNumbers} number add{numberLimit.maxNumbers === 1 ? "" : "s"} per billing cycle ({numberLimit.current} used this cycle). Disconnecting a number does not free a slot until the plan renews.
                  {!numberLimit.canAdd
                    ? ` Buy an extra add for ₹${numberLimit.addonBase || 150}+GST, or wait until the next cycle.`
                    : ""}
                </p>
              </div>
              {!numberLimit.canAdd && (
                <button
                  type="button"
                  className="ping-btn ping-btn-green ping-btn-sm"
                  disabled={buyingExtraNumber}
                  onClick={handleBuyExtraNumber}
                >
                  {buyingExtraNumber ? "Opening checkout…" : `Buy extra number · ₹${numberLimit.addonBase || 150}`}
                </button>
              )}
            </div>
          ) : null}

          {connected && accounts.length > 0 && (
            <div className="waba-kpis">
              <div className="waba-kpi-card">
                <div className="k">Total accounts</div>
                <div className="v">{new Set(accounts.map((a) => a.waba_id || a.id)).size}</div>
              </div>
              <div className="waba-kpi-card">
                <div className="k">Phone numbers</div>
                <div className="v">{accounts.length}</div>
              </div>
              <div className="waba-kpi-card">
                <div className="k">Active numbers</div>
                <div className="v">{accounts.filter((a) => !hasProcurementIssue(a)).length}</div>
              </div>
              {numberLimit && !needsPlan ? (
                <div className="waba-kpi-card">
                  <div className="k">Adds this cycle</div>
                  <div className="v">{numberLimit.current}/{numberLimit.maxNumbers}</div>
                </div>
              ) : null}
            </div>
          )}

        <ConnectResultBanner
          result={connectResult}
          onDismiss={() => setConnectResult(null)}
        />

        {pageError && (
          <div className="ping-banner red" style={{ marginBottom: 14 }}>
            <div>
              <b>{pageError}</b>
              <button type="button" onClick={handleRetry} className="ping-btn ping-btn-ghost ping-btn-sm" style={{ marginTop: 8 }}>
                Try again
              </button>
            </div>
          </div>
        )}

        {fetching && accounts.length === 0 && (
          <div className="waba-empty">
            <p style={{ color: "var(--text-2)", fontSize: 14 }}>Loading accounts…</p>
          </div>
        )}

        {!connected && !fetching && !pageError && accounts.length === 0 && (
          <div className="waba-empty">
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 6px" }}>
              Connect your WhatsApp Business
            </h3>
            <p style={{ color: "var(--text-2)", fontSize: 13.5, maxWidth: 420, margin: "0 auto 16px" }}>
              {needsPlan
                ? "Pay for a platform plan first. Wallet money only covers messages after a number is connected."
                : "Use Facebook for guided setup, or enter API credentials manually — both paths register your number and configure webhooks automatically."}
            </p>
            <button
              type="button"
              onClick={needsPlan ? () => setSubscribePromptOpen(true) : (atPlanCap ? handleBuyExtraNumber : openConnectPicker)}
              disabled={Boolean(overlayType) || buyingExtraNumber}
              className="ping-btn ping-btn-primary disabled:opacity-50"
            >
              {needsPlan
                ? "Subscribe to add a number"
                : atPlanCap
                ? (buyingExtraNumber ? "Opening checkout…" : `Buy extra number · ₹${numberLimit.addonBase || 150}`)
                : "Get Started"}
            </button>
          </div>
        )}

        {connected && accounts.length > 0 && !fetching && (
          <div className="waba-rows">
            {accounts.map((account) => {
              const qTone = qualityTone(account.quality_rating);
              const nameSt =
                account.new_name_status &&
                account.new_name_status !== "NONE" &&
                account.new_name_status !== "APPROVED"
                  ? account.new_name_status
                  : account.name_status;
              const nTone = nameStatusTone(nameSt);
              const pending = hasProcurementIssue(account);

              return (
                <div key={account.phone_id || account.id} className="waba-row-wrap">
                  <div className="waba-row">
                    <div className="waba-row-id">
                      <PhoneProfileAvatar
                        phoneId={account.phone_id}
                        profilePictureUrl={account.profile_picture_url}
                        sizeClass="w-10 h-10"
                      />
                      <div className="who">
                        <b>
                          {account.phone_name ||
                            account.verified_name ||
                            "Unnamed Account"}
                        </b>
                        <span className="num">{account.phone_number}</span>
                        <span className="waba">
                          {account.waba_name || account.waba_id || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="waba-row-meta">
                      <div className="waba-statbox">
                        <span>Quality</span>
                        <b className={`ping-chip ${chipTone(qTone)}`} style={{ padding: "1px 7px", fontSize: 11 }}>
                          {formatQualityRating(account.quality_rating)}
                        </b>
                      </div>
                      <div className="waba-statbox">
                        <span>Limit</span>
                        <b>{formatMessagingLimit(account.messaging_limit)}</b>
                      </div>
                      <div className="waba-statbox">
                        <span>Name</span>
                        <b className={`ping-chip ${chipTone(nTone)}`} style={{ padding: "1px 7px", fontSize: 11 }}>
                          {formatNameStatus(nameSt)}
                        </b>
                      </div>
                    </div>

                    <div className="waba-row-actions">
                      <ConnectionTypeBadge connectionType={account.connection_type} />
                      <span className={`ping-chip ${pending ? "a" : "g"}`}>
                        {pending ? "Setup pending" : "Active"}
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/waba/${account.phone_id}`)}
                        className="ping-btn ping-btn-primary ping-btn-sm"
                      >
                        Manage
                      </button>
                      {account.connection_type === "manual" && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleRefreshToken(account.phone_id)}
                            disabled={refreshingPhoneId === account.phone_id}
                            className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-50"
                          >
                            {refreshingPhoneId === account.phone_id ? "Refreshing…" : "Refresh"}
                          </button>
                          <button
                            type="button"
                            onClick={() => openManualConnect(account.phone_id)}
                            className="ping-btn ping-btn-ghost ping-btn-sm"
                          >
                            Token
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeletePhoneNumber(account.phone_id)}
                        className="ping-btn ping-btn-danger ping-btn-sm"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>

                  {pending && (
                    <div className="waba-row-alert">
                      <div>
                        <b>
                          {String(account.webhook_registration_error || "")
                            .toLowerCase()
                            .includes("payment method") ||
                          String(account.webhook_registration_error || "")
                            .toLowerCase()
                            .includes("other whatsapp provider")
                            ? "Already connected to another provider"
                            : "WhatsApp setup incomplete"}
                        </b>
                        <p>{getProcurementMessage(account)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRetryAisensy(account.phone_id)}
                        disabled={retryingPhoneId === account.phone_id}
                        className="ping-btn ping-btn-ghost ping-btn-sm disabled:opacity-50"
                      >
                        {retryingPhoneId === account.phone_id
                          ? "Setting up..."
                          : "Retry setup"}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SubscribeRequiredPrompt
        open={subscribePromptOpen}
        onClose={() => setSubscribePromptOpen(false)}
      />

      <ConnectMethodPicker
        open={connectPickerOpen}
        onClose={() => setConnectPickerOpen(false)}
        onSelectFacebook={handleEmbeddedSignup}
        onSelectManual={() => openManualConnect()}
      />

      <ManualConnectModal
        open={manualModalOpen}
        onClose={closeManualConnect}
        onConnectStart={handleManualConnectStart}
        onConnectEnd={handleManualConnectEnd}
        onConnectError={handleManualConnectError}
        onSuccess={handleManualConnectSuccess}
        editPhoneId={editPhoneId}
      />

      <AisensyBusinessSetupModal
        open={aisensyBizModalOpen}
        onClose={() => setAisensyBizModalOpen(false)}
        onSubmit={handleAisensyBusinessSubmit}
        submitting={aisensyBizSubmitting}
      />

      <WhatsappConnectOverlay type={overlayType} />
    </div>
  );
};

export default WhatsappBusiness;
